import { useEffect, useId, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { ReportDetail } from '@tallyframe/core';
import {
  usePublishReport,
  useRepublishReport,
  useSetPublicationAutoUpdate,
  useUnpublishReport
} from '../api/report-queries';
import { useSession } from '../app/session-context';
import { ApiError } from '../api/errors';
import { ErrorBanner } from '../components/ErrorBanner';
import { PlanLimitMessage, upgradeHref } from '../components/PlanLimitMessage';
import { Button } from '../ui/Button';
import { Switch } from '../ui/Checkbox';
import { CopyButton } from '../ui/CopyButton';
import { ConfirmDialog, Dialog } from '../ui/Dialog';
import { Row, Stack } from '../ui/Stack';

/** Shown verbatim in the share dialog before anything is published (spec §4, "Publish UI"). */
export const EXPOSURE_SENTENCE =
  'Anyone with the link can see this report and the data behind its charts and tables.';

const NO_DATA_COPY = 'Load data before publishing.';
const UNPUBLISH_MESSAGE = 'The link stops working straight away. You can publish again later to get a new link.';
const AUTO_UPDATE_LABEL = 'Keep the link up to date with new data';
const AUTO_UPDATE_HINT = 'New data goes live when it arrives. Layout and wording changes still wait for an update.';

/**
 * Share button and dialog: publish, update and unpublish, driven by
 * `detail.publication`. The parent reloads the report from `onChanged`, so
 * this holds no copy of the publication state of its own.
 */
export function ShareControl(props: {
  detail: ReportDetail;
  onChanged(): void;
  /** Open the dialog when this turns on (the `share=1` URL). Only the first
   *  turn-on opens it: the dialog is not reopened if the flag toggles off and on. */
  openOnLoad?: boolean;
}): ReactElement {
  const { detail, onChanged, openOnLoad } = props;
  const { snapshot } = useSession();
  const workspaceId = snapshot.workspaceId ?? '';
  const upgradeUrl = upgradeHref(snapshot.config?.upgradeUrl ?? null, snapshot);
  const pub = detail.publication ?? null;
  const reportId = detail.report.id;
  const revision = detail.report.revision;
  const hasData = Boolean(detail.freshness.activeSnapshotId);

  const publish = usePublishReport(workspaceId);
  const republish = useRepublishReport(workspaceId);
  const unpublish = useUnpublishReport(workspaceId);
  const autoUpdate = useSetPublicationAutoUpdate(workspaceId);
  const busy = publish.isPending || republish.isPending || unpublish.isPending || autoUpdate.isPending;
  const behind = pub?.behind === true;

  const triggerRef = useRef<HTMLSpanElement>(null);
  const hintId = useId();
  const [shareOpen, setShareOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Both dialogs are controlled and the unpublish confirmation replaces the
  // share dialog, so focus is returned to the Share button by hand.
  function focusShare(): void {
    window.setTimeout(() => triggerRef.current?.querySelector('button')?.focus(), 0);
  }

  function changeShareOpen(open: boolean): void {
    setShareOpen(open);
    if (open) setError(null);
    else focusShare();
  }

  const openedOnLoad = useRef(false);
  useEffect(() => {
    if (!openOnLoad || openedOnLoad.current) return;
    openedOnLoad.current = true;
    changeShareOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot
  }, [openOnLoad]);

  function changeConfirmOpen(open: boolean): void {
    setConfirmOpen(open);
    if (!open) focusShare();
  }

  async function run(action: () => Promise<unknown>, reopen: boolean): Promise<void> {
    setError(null);
    try {
      await action();
      onChanged();
    } catch (cause) {
      setError(cause);
      if (reopen) setShareOpen(true);
    }
  }

  return (
    <>
      <span ref={triggerRef} className="inline-flex">
        <Button
          onPress={() => changeShareOpen(true)}
          aria-label={behind ? 'Share, published version is behind' : undefined}
        >
          Share
          {behind ? (
            <span
              aria-hidden="true"
              data-testid="share-behind-dot"
              className="inline-block w-2 h-2 m-0 p-0 rounded-pill bg-warn"
            />
          ) : null}
        </Button>
      </span>
      {behind ? (
        <Button
          variant="secondary"
          isDisabled={busy}
          onPress={() => void run(() => republish.mutateAsync({ reportId, expectedRevision: revision }), true)}
        >
          Update live link
        </Button>
      ) : null}
      <Dialog title="Share this report" size="sm" isOpen={shareOpen} onOpenChange={changeShareOpen}>
        <Stack gap={4}>
          {ApiError.isApiError(error) && error.code === 'PLAN_LIMIT_EXCEEDED' ? (
            <PlanLimitMessage error={error} upgradeUrl={upgradeUrl} />
          ) : error ? (
            <ErrorBanner error={error} />
          ) : null}
          {pub ? (
            <>
              <Stack gap={2}>
                <p className="m-0 p-0 text-md font-ui text-ink break-all">{pub.url}</p>
                {pub.behind ? (
                  <p className="m-0 p-0 text-sm font-ui text-muted">Published version is behind</p>
                ) : null}
              </Stack>
              <Stack gap={1}>
                <Switch
                  isSelected={pub.autoUpdate}
                  isDisabled={busy}
                  onChange={(selected) =>
                    void run(() => autoUpdate.mutateAsync({ reportId, autoUpdate: selected }), false)
                  }
                >
                  <span className="text-md font-ui text-ink">{AUTO_UPDATE_LABEL}</span>
                </Switch>
                <p className="m-0 p-0 text-sm font-ui text-muted">{AUTO_UPDATE_HINT}</p>
              </Stack>
              <Row gap={2}>
                <CopyButton value={pub.url} label="Copy" />
                {pub.behind ? (
                  <Button
                    variant="primary"
                    isDisabled={busy}
                    onPress={() =>
                      void run(() => republish.mutateAsync({ reportId, expectedRevision: revision }), false)
                    }
                  >
                    Update published version
                  </Button>
                ) : null}
                <Button
                  variant="danger"
                  isDisabled={busy}
                  onPress={() => {
                    setShareOpen(false);
                    setConfirmOpen(true);
                  }}
                >
                  Unpublish
                </Button>
              </Row>
            </>
          ) : (
            <>
              <p className="m-0 p-0 text-md font-ui text-ink">{EXPOSURE_SENTENCE}</p>
              <Row gap={2}>
                <Button
                  variant="primary"
                  isDisabled={!hasData || busy}
                  aria-describedby={hasData ? undefined : hintId}
                  onPress={() =>
                    void run(() => publish.mutateAsync({ reportId, expectedRevision: revision }), false)
                  }
                >
                  Publish link
                </Button>
              </Row>
              {hasData ? null : (
                <span id={hintId} className="sr-only">
                  {NO_DATA_COPY}
                </span>
              )}
            </>
          )}
        </Stack>
      </Dialog>
      <ConfirmDialog
        title="Unpublish this report?"
        message={UNPUBLISH_MESSAGE}
        confirmLabel="Unpublish"
        tone="danger"
        isOpen={confirmOpen}
        onOpenChange={changeConfirmOpen}
        onConfirm={() => void run(() => unpublish.mutateAsync({ reportId }), true)}
      />
    </>
  );
}
